import React, { useState, useEffect, useRef } from 'react';
import { 
  getUserConversations, 
  getConversation, 
  createConversation, 
  updateConversationName, 
  deleteConversation,
  addMessageToConversation,
  clearConversationMessages
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import './DeepSeekChat.css';

// Helper function to split text into lines
const splitIntoLines = (text) => {
  return text.split(/\\r?\\n/).filter(line => line.trim() !== "");
};

const DeepSeekChat = () => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [newChatName, setNewChatName] = useState('');
  const [showNewChatInput, setShowNewChatInput] = useState(false);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);

  // Load conversations from server on component mount
  useEffect(() => {
    loadConversations();
    
    // Force conversation loading when component mounts
    const initTimeout = setTimeout(() => {
      console.log('Forcing conversation load on mount');
      loadConversations();
    }, 1000);

    // Add additional forced re-renders to ensure conversations show up
    const secondTimeout = setTimeout(() => {
      console.log('Force second conversation load');
      loadConversations();
      
      // Also force styles to be applied
      document.querySelectorAll('.conversation-item').forEach(item => {
        item.style.display = 'flex';
        item.style.visibility = 'visible';
        item.style.opacity = '1';
      });
      
      const list = document.querySelector('.conversations-list');
      if (list) {
        list.style.display = 'block';
        list.style.visibility = 'visible';
        list.style.opacity = '1';
      }
    }, 2000);
    
    return () => {
      clearTimeout(initTimeout);
      clearTimeout(secondTimeout);
    };
  }, [user]);
  
  // Load conversations from the server
  const loadConversations = async () => {
    try {
      setIsLoading(true);
      
      // Get conversations from server
      const serverConversations = await getUserConversations();
      console.log('Loaded conversations from server:', serverConversations);
      
      if (serverConversations && serverConversations.length > 0) {
        // Conversations are already sorted by lastUpdated on the server
        setConversations(serverConversations);
        console.log('Setting conversations in state:', serverConversations.length);
        
        // Activate the most recent conversation
        const mostRecentConversation = serverConversations[0];
        setActiveConversation(mostRecentConversation._id);
        setChatHistory(mostRecentConversation.messages || []);
        
        // Force conversation items to be visible through direct DOM manipulation
        setTimeout(() => {
          console.log('Forcing conversation visibility');
          document.querySelectorAll('.conversation-item').forEach(item => {
            item.style.display = 'flex';
            item.style.visibility = 'visible';
            item.style.opacity = '1';
            item.style.animation = 'none';
          });
          
          const list = document.querySelector('.conversations-list');
          if (list) {
            list.style.display = 'block';
            list.style.visibility = 'visible';
            list.style.opacity = '1';
            list.style.height = 'auto';
            list.style.maxHeight = '1000px';
          }
          
          // Also ensure titles are visible
          document.querySelectorAll('.conversation-title').forEach(title => {
            title.style.display = 'block';
            title.style.visibility = 'visible';
            title.style.opacity = '1';
          });
        }, 200);
        
        // Check again after a longer delay to ensure conversations are visible
        setTimeout(() => {
          console.log('Checking conversations visibility again');
          // Get all conversation items and check if any are visible
          const conversationItems = document.querySelectorAll('.conversation-item');
          const visibleItems = Array.from(conversationItems).filter(
            item => {
              const style = window.getComputedStyle(item);
              return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
            }
          );
          
          if (visibleItems.length === 0) {
            // Try toggling display on parent to refresh rendering
            const sidebar = document.querySelector('.chat-sidebar');
            if (sidebar) {
              const originalDisplay = sidebar.style.display;
              sidebar.style.display = 'none';
              setTimeout(() => {
                sidebar.style.display = originalDisplay || 'flex';
                
                // Force conversation items again
                document.querySelectorAll('.conversation-item').forEach(item => {
                  item.style.display = 'flex';
                  item.style.visibility = 'visible';
                  item.style.opacity = '1';
                });
              }, 50);
            }
          }
        }, 1000);
      } else {
        // Create a default conversation if none exist
        console.log('No conversations found, creating default');
        const newChat = await createConversation('New Chat');
        setConversations([newChat]);
        setActiveConversation(newChat._id);
        setChatHistory(newChat.messages || []);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      // If there's an error, we'll create a new conversation 
      // when the user sends their first message
    } finally {
      setIsLoading(false);
    }
  };
  
  // Focus input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  // Scroll to bottom of chat whenever history changes
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);
  
  // Create a new conversation
  const handleCreateNewConversation = async (name) => {
    try {
      setIsLoading(true);
      
      // Create new conversation on the server
      const newConversation = await createConversation(name || 'New Chat');
      
      // Add to local state
      setConversations(prev => [newConversation, ...prev]);
      
      // Switch to the new conversation
      setActiveConversation(newConversation._id);
      setChatHistory(newConversation.messages || []);
      
      // Reset UI state
      setShowNewChatInput(false);
      setNewChatName('');
    } catch (error) {
      console.error('Error creating new conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Delete a conversation
  const handleDeleteConversation = async (id) => {
    try {
      // Delete conversation on the server
      await deleteConversation(id);
      
      // Update local state
      const updatedConversations = conversations.filter(conv => conv._id !== id);
      setConversations(updatedConversations);
      
      // If we're deleting the active conversation, switch to the most recent one
      if (id === activeConversation) {
        if (updatedConversations.length > 0) {
          setActiveConversation(updatedConversations[0]._id);
          setChatHistory(updatedConversations[0].messages || []);
        } else {
          // If no conversations left, create a new one
          handleCreateNewConversation('New Chat');
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };
  
  // Switch to a different conversation
  const handleSwitchConversation = async (id) => {
    try {
      if (id === activeConversation) return; // Already on this conversation
      
      setIsLoading(true);
      
      // Get the full conversation from the server (to ensure we have the latest messages)
      const conversation = await getConversation(id);
      
      if (conversation) {
        setActiveConversation(id);
        setChatHistory(conversation.messages || []);
      }
    } catch (error) {
      console.error('Error switching conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear the current chat
  const handleClearChat = async () => {
    if (!activeConversation) return;
    
    try {
      setIsLoading(true);
      
      // Clear conversation messages on the server
      const updatedConversation = await clearConversationMessages(activeConversation);
      
      // Update the chat history with the cleared state
      setChatHistory(updatedConversation.messages || []);
      
      // Update the conversation in our local state
      setConversations(prev => 
        prev.map(conv => 
          conv._id === activeConversation 
            ? { ...conv, messages: updatedConversation.messages || [] }
            : conv
        )
      );
    } catch (error) {
      console.error('Failed to clear chat history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Send a message and get AI response
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    try {
      // Create a conversation if none exists
      if (!activeConversation) {
        await handleCreateNewConversation('New Chat');
      }
      
      setIsLoading(true);
      const currentMessage = message;
      setMessage(''); // Clear input field immediately for better UX
      
      // Show user message immediately (optimistic UI)
      const userMessage = { isUser: true, content: currentMessage };
      setChatHistory(prev => [...prev, userMessage]);
      
      try {
        // Send message to server and get AI response
        const response = await addMessageToConversation(
          activeConversation,
          currentMessage,
          'general'
        );
        
        console.log('Server response:', response);
        
        // If successful, the server returns both messages and updated conversation
        if (response && response.conversation) {
          // Update conversations list with the updated conversation
          setConversations(prev => 
            prev.map(conv => 
              conv._id === activeConversation ? response.conversation : conv
            ).sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
          );
          
          // DISABLE ALL ANIMATIONS - Just display messages immediately
          const modifiedMessages = [...response.conversation.messages];
          
          // Mark all messages as not new to prevent animation
          if (modifiedMessages.length > 0) {
            modifiedMessages.forEach((msg, i) => {
              // Ensure no message gets animated
              modifiedMessages[i] = { 
                ...msg, 
                isNew: false, 
                isHistory: true,
                _forceAnimation: false  // Make sure animation is disabled
              };
            });
            
            console.log('All animations disabled, messages will display immediately');
          }
          
          // Update chat history with no animation flags
          setChatHistory(modifiedMessages || []);
          
          // Log the chat history to verify isNew flag is set
          console.log('Chat history after update:', modifiedMessages);
        } else if (response && response.userMessage && response.aiMessage) {
          // Add the AI message WITHOUT animation flags
          const aiMessageWithoutAnimation = { 
            ...response.aiMessage, 
            isNew: false,
            isHistory: true,
            _forceAnimation: false
          };
          console.log('Adding single AI message without animation:', aiMessageWithoutAnimation);
          setChatHistory(prev => [...prev, aiMessageWithoutAnimation]);
        } else {
          // Handle unexpected response format
          console.error('Unexpected response format from server:', response);
          throw new Error('Unexpected response format from server');
        }
      } catch (apiError) {
        console.error('API error:', apiError);
        
        // Get the error message to display
        let errorMessage = "I'm sorry, I encountered an error. Please try again later.";
        if (apiError.message) {
          errorMessage = apiError.message;
        }
        
        // Add error message to chat
        setChatHistory(prev => [
          ...prev,
          { 
            isUser: false, 
            content: errorMessage
          }
        ]);
        
        // Log detailed error if available
        if (apiError.originalError) {
          console.error('Original error details:', apiError.originalError);
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Add error message to chat
      setChatHistory(prev => [
        ...prev,
        { 
          isUser: false, 
          content: "I'm sorry, I encountered an unexpected error. The system might be temporarily unavailable." 
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="deepseek-chat-page">
      <div className="chat-sidebar">
        <div className="sidebar-header">
          <h2>White Rabbit</h2>
          <button 
            className="new-chat-btn"
            onClick={() => setShowNewChatInput(true)}
          >
            + New Chat
          </button>
        </div>
        
        {showNewChatInput && (
          <div className="new-chat-input-container">
            <input
              type="text"
              value={newChatName}
              onChange={(e) => setNewChatName(e.target.value)}
              placeholder="Enter chat name..."
              className="new-chat-input"
              autoFocus
            />
            <div className="new-chat-actions">
              <button 
                onClick={() => handleCreateNewConversation(newChatName)}
                disabled={!newChatName.trim()}
              >
                Create
              </button>
              <button onClick={() => setShowNewChatInput(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        
        <div className="conversations-list">
          {conversations.map((conv, index) => (
            <div 
              key={conv._id} 
              className={`conversation-item ${conv._id === activeConversation ? 'active' : ''}`}
              onClick={() => handleSwitchConversation(conv._id)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="conversation-title">
                {conv.name}
              </div>
              <button 
                className="delete-conversation-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if(window.confirm(`Delete conversation "${conv.name}"?`)) {
                    handleDeleteConversation(conv._id);
                  }
                }}
                title="Delete conversation"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
      
      <div className="chat-main">
        <div className="chat-container">
          <div className="chat-header">
            <h1>White Rabbit</h1>
            <div className="chat-actions">
              <button 
                onClick={handleClearChat} 
                className="clear-chat-btn"
                title="Clear current conversation"
                disabled={!activeConversation || isLoading}
                aria-label="Delete conversation"
              >
                <span className="bin-icon">🗑️</span>
              </button>
              <button 
                onClick={() => handleCreateNewConversation('New Chat')} 
                className="new-chat-mobile-btn"
                title="Start new conversation"
                disabled={isLoading}
              >
                New Chat
              </button>
            </div>
          </div>
          
          <div className="chat-messages" ref={chatContainerRef}>
            {chatHistory.map((msg, index) => (
              <div 
                key={index} 
                className={`chat-message ${msg.isUser ? 'user-message' : 'ai-message'}`}
                data-is-new={msg.isNew ? 'true' : 'false'}
              >
                {msg.isUser ? (
                  <div className="message-content">{msg.content}</div>
                ) : (
                  <div className="message-content">
                    <div className="cyber-text-container">
                      {splitIntoLines(msg.content).map((line, lineIndex) => (
                        <div 
                          key={lineIndex} 
                          className="cyber-text-line"
                          style={{
                            display: 'block',
                            visibility: 'visible',
                            opacity: 1,
                            animation: 'none',
                            transition: 'none'
                          }}
                        >
                          {line}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
{/* Loading indicator removed */}
          </div>
          
          <form onSubmit={handleSendMessage} className="chat-input">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ask White Rabbit anything..."
              ref={inputRef}
              disabled={isLoading}
            />
            <button 
              type="submit" 
              disabled={isLoading || !message.trim()}
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DeepSeekChat;